import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { FormlyTypes, FormlyValidator } from '@features/applications/enums/formly-types.enum';
import { dtoKeyArrayRegex, emailRegex, ipv4OrIpv6Regex, semverRegex, urlRegex } from '@shared/utils/regex.util';
import { TypeOption } from 'node_modules/@ngx-formly/core/lib/models/config';
import { ColorPickerInputTypeComponent } from './color-picker/color-picker.component';
import { DroppableDropdownTypeComponent } from './droppable-dropdown/droppable-dropdown.component';
import { FormlyApplicationActionParametersRepeatComponent } from './formly-application-action-parameters-repeat/formly-application-action-parameters-repeat.component';
import { FormlyApplicationAuthParamsTypeComponent } from './formly-application-auth-params-type/formly-application-auth-params-type.component';
import { FormlyAutoCompleteTypeComponent } from './formly-auto-complete-type/formly-auto-complete-type.component';
import { FormlyButtonTypeComponent } from './formly-button-type/formly-button-type.component';
import { FormlyCheckboxTypeComponent } from './formly-checkbox-type/formly-checkbox-type.component';
import { FormlyChipsTypeComponent } from './formly-chips-type/formly-chips-type.component';
import { FormlyDateTimeRangeTypeComponent } from './formly-date-time-range-type/formly-date-time-range-type.component';
import { FormlyDtoInputTypeComponent } from './formly-dto-input-type/formly-dto-input-type.component';
import { FormlyDtoKeyInputTypeComponent } from './formly-dto-key-input-type/formly-dto-key-input-type.component';
import { FormlyInputTypeComponent } from './formly-input-type/formly-input-type.component';
import { FormlyLabelTypeComponent } from './formly-label-type/formly-label-type.component';
import { FormlyNodeIfConditionRepeatComponent } from './formly-node-if-condition-repeat/formly-node-if-condition-repeat.component';
import { FormlyNumberFormatTypeComponent } from './formly-number-format-type/formly-number-format-type.component';
import { FormlyPasswordTypeComponent } from './formly-password-type/formly-password-type.component';
import { FormlyPasswordWithConfirmTypeComponent } from './formly-password-with-confirm-type/formly-password-with-confirm-type.component';
import { FormlyRadioTypeComponent } from './formly-radio-type/formly-radio-type.component';
import { FormlyRecursiveParameterTypeComponent } from './formly-recursive-parameter-type/formly-recursive-parameter-type.component';
import { FormlySchedulerTypeComponent } from './formly-scheduler-type/formly-scheduler-type.component';
import { FormlySimpleRepeatTypeComponent } from './formly-simple-repeat-type/formly-simple-repeat-type.component';
import { FormlyHiddenTypeComponent } from './hidden-input/hidden-input.component';
import { TextEditorTypeComponent } from './text-editor-type/text-editor-type.component';
import { FormlyCheckboxWrapperComponent } from './wrappers/checkbox-wrapper.component';
import { FormlyFieldWithAddButtonWrapperComponent } from './wrappers/field-with-add-wrapper.component';
import { FormlyHiddenWrapperComponent } from './wrappers/hidden-wrapper.component';
import { FormlySectionWrapperComponent } from './wrappers/section-wrapper.component';

export function urlValidator(control: AbstractControl<string>): ValidationErrors {
  return urlRegex.test(control.value) ? null : { url: true };
}

export function versionValidator(control: AbstractControl<string>): ValidationErrors {
  return semverRegex.test(control.value) ? null : { version: true };
}

export function dtoKeyValidator(control: AbstractControl<string>): ValidationErrors {
  if (
    (control.value?.startsWith('{{') && !control.value?.endsWith('}}')) ||
    (control.value?.startsWith('{{') && control.value?.endsWith('}}') && !control.value.match(/{{([^}]+)}}/g))
  ) {
    return { dtoKey: true };
  }

  return null;
}

export function emailOrTemplateVariableValidator(control: AbstractControl): ValidationErrors {
  const controlValue = control.value as string[];
  const validCondition = (email: string) => {
    return emailRegex.test(email) || (email.startsWith('{{') && email.endsWith('}}') && email.match(/{{([^}]+)}}/g));
  };

  if (!controlValue || controlValue?.length < 1) {
    return null;
  }

  if (Array.isArray(controlValue)) {
    const valid = controlValue?.every((email) => {
      return validCondition(email);
    });

    return valid ? null : { emailOrTemplateVariable: true };
  }

  const value = controlValue as string;

  if (validCondition(value)) {
    return null;
  }

  return { emailOrTemplateVariable: true };
}

export function dtoKeyArrayValidator(control: AbstractControl<string>): ValidationErrors {
  const match = control.value?.toString().trim().match(dtoKeyArrayRegex);

  return match?.length ? null : { dtoKeyArray: true };
}

export function ipValidator(control: AbstractControl): ValidationErrors {
  return !control.value || ipv4OrIpv6Regex.test(control.value) ? null : { ip: true };
}

export function emailArrayValidator(control: AbstractControl): ValidationErrors | null {
  const controlValue = control.value as string[];

  if (!controlValue || controlValue?.length < 1) {
    return null;
  }

  const valid = controlValue?.every((email) => {
    return emailRegex.test(email);
  });

  return valid ? null : { email: true };
}

export const formlyTypes: TypeOption[] = [
  { name: FormlyTypes.repeat, component: FormlySimpleRepeatTypeComponent, wrappers: ['cap-form-field'] },
  {
    name: FormlyTypes.nodeIfCondition,
    component: FormlyNodeIfConditionRepeatComponent,
  },
  {
    name: FormlyTypes.applicationAuthParams,
    component: FormlyApplicationAuthParamsTypeComponent,
  },
  {
    name: FormlyTypes.colorPicker,
    component: ColorPickerInputTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.droppableDropdown,
    component: DroppableDropdownTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.dtoInput,
    component: FormlyDtoInputTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.label,
    component: FormlyLabelTypeComponent,
  },
  {
    name: FormlyTypes.scheduler,
    component: FormlySchedulerTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.neqtoInput,
    component: FormlyInputTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.hidden,
    component: FormlyHiddenTypeComponent,
    wrappers: ['hidden'],
  },
  {
    name: FormlyTypes.numberFormat,
    component: FormlyNumberFormatTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.password,
    component: FormlyPasswordTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.dtoKeyInput,
    component: FormlyDtoKeyInputTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.applicationActionParams,
    component: FormlyApplicationActionParametersRepeatComponent,
  },
  {
    name: FormlyTypes.button,
    component: FormlyButtonTypeComponent,
  },
  {
    name: FormlyTypes.recursiveApplicationActionParams,
    component: FormlyRecursiveParameterTypeComponent,
  },
  {
    name: FormlyTypes.checkbox,
    component: FormlyCheckboxTypeComponent,
    wrappers: ['checkbox'],
  },
  {
    name: FormlyTypes.radio,
    component: FormlyRadioTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.passwordWithConfirm,
    component: FormlyPasswordWithConfirmTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.autoComplete,
    component: FormlyAutoCompleteTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.chips,
    component: FormlyChipsTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.textEditor,
    component: TextEditorTypeComponent,
    wrappers: ['cap-form-field'],
  },
  {
    name: FormlyTypes.dateTimeRange,
    component: FormlyDateTimeRangeTypeComponent,
    wrappers: ['cap-form-field'],
  },
];

export const formlyValidators = [
  {
    name: FormlyValidator.url,
    validation: urlValidator,
  },
  {
    name: FormlyValidator.version,
    validation: versionValidator,
  },
  {
    name: FormlyValidator.dtoKey,
    validation: dtoKeyValidator,
  },
  {
    name: FormlyValidator.dtoKeyArray,
    validation: dtoKeyArrayValidator,
  },
  {
    name: FormlyValidator.email,
    validation: Validators.email,
  },
  {
    name: FormlyValidator.ip,
    validation: ipValidator,
  },
  {
    name: FormlyValidator.emailArray,
    validation: emailArrayValidator,
  },
  {
    name: FormlyValidator.emailOrTemplateVariable,
    validation: emailOrTemplateVariableValidator,
  },
];

export const formlyWrappers = [
  { name: 'hidden', component: FormlyHiddenWrapperComponent },
  { name: 'checkbox', component: FormlyCheckboxWrapperComponent },
  { name: 'section', component: FormlySectionWrapperComponent },
  { name: 'field-with-add-button', component: FormlyFieldWithAddButtonWrapperComponent },
];
